import React, { useState, useEffect, useCallback } from 'react';
import { Play, RefreshCw, Loader2, Plus, Trash2, ExternalLink, Coins, Train, RotateCcw } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';

const API = process.env.REACT_APP_BACKEND_URL;

function StatusBadge({ status }) {
  const colors = {
    running: 'bg-green-500/20 text-green-400 border-green-500/30',
    stopped: 'bg-gray-500/20 text-gray-400 border-gray-500/30',
    building: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
    error: 'bg-red-500/20 text-red-400 border-red-500/30',
    pending: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30'
  };
  const labels = { 
    running: '● Activo', 
    stopped: '○ Detenido', 
    building: '◐ Construyendo', 
    error: '✕ Error', 
    pending: '○ Pendiente' 
  };
  return (
    <span className={`px-2 py-0.5 text-xs rounded-full border ${colors[status] || colors.pending}`}>
      {labels[status] || status}
    </span>
  );
}

function DeploymentItem({ dep, onDelete, onRedeploy, onRefresh }) {
  const isBuilding = dep.status === 'building' || dep.status === 'pending';
  const hasUrl = dep.url && dep.url.startsWith('http');
  
  return (
    <div className="p-4 rounded-xl bg-slate-800/50 border border-slate-700">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <Train className="w-4 h-4 text-purple-400" />
          <span className="text-sm text-white font-medium">{dep.project_name || dep.railway_project_name}</span>
          <StatusBadge status={dep.status} />
        </div>
        <div className="flex items-center gap-1">
          {isBuilding && (
            <button onClick={onRefresh} className="p-1.5 rounded hover:bg-blue-500/20 text-blue-400">
              <RefreshCw className="w-4 h-4" />
            </button>
          )}
          {dep.status === 'running' && (
            <button onClick={() => onRedeploy(dep.id)} className="p-1.5 rounded hover:bg-purple-500/20 text-purple-400" title="Redesplegar">
              <RotateCcw className="w-4 h-4" />
            </button>
          )}
          <button onClick={() => onDelete(dep.id)} className="p-1.5 rounded hover:bg-red-500/20 text-red-400">
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      </div>
      {hasUrl && (
        <a href={dep.url} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 text-xs text-purple-400 hover:underline mb-2">
          <ExternalLink className="w-3 h-3" />{dep.url}
        </a>
      )}
      <div className="flex items-center gap-3 text-xs text-gray-500">
        {dep.railway_project_name && <span>Proyecto: {dep.railway_project_name}</span>}
        {dep.credits_charged && (
          <span className="flex items-center gap-1">
            <Coins className="w-3 h-3" />{dep.credits_charged} créditos
          </span>
        )}
      </div>
      <p className="text-xs text-gray-600 mt-1">{new Date(dep.created_at).toLocaleString('es')}</p>
      
      {/* Logs */}
      {dep.logs && dep.logs.length > 0 && (
        <div className="mt-3 p-2 rounded bg-slate-900/50 max-h-32 overflow-y-auto">
          {dep.logs.slice(-5).map((log, i) => (
            <p key={i} className="text-xs text-gray-400 font-mono">{log.message}</p>
          ))}
        </div>
      )}
    </div>
  );
}

function RailwayPanel({ projectId, onClose }) {
  const { user, refreshUser } = useAuth();
  const [railwayStatus, setRailwayStatus] = useState(null);
  const [deployments, setDeployments] = useState([]);
  const [envVars, setEnvVars] = useState({});
  const [newVarKey, setNewVarKey] = useState('');
  const [newVarValue, setNewVarValue] = useState('');
  const [loading, setLoading] = useState(true);
  const [deploying, setDeploying] = useState(false);

  const fetchData = useCallback(async () => {
    if (!projectId) return;
    setLoading(true);
    const token = localStorage.getItem('cloudtexa_token');
    const headers = { Authorization: `Bearer ${token}` };
    try {
      const [statusRes, depsRes] = await Promise.all([
        axios.get(`${API}/api/railway/status`),
        axios.get(`${API}/api/railway/deployments`, { headers })
      ]);
      setRailwayStatus(statusRes.data);
      setDeployments(depsRes.data.deployments.filter(d => d.project_id === projectId));
    } catch (err) {
      console.error(err);
    }
    setLoading(false);
  }, [projectId]);

  useEffect(() => { 
    fetchData(); 
  }, [fetchData]);

  // Poll for building deployments
  useEffect(() => {
    const buildingDeps = deployments.filter(d => d.status === 'building' || d.status === 'pending');
    if (buildingDeps.length === 0) return;
    const interval = setInterval(fetchData, 5000);
    return () => clearInterval(interval);
  }, [deployments, fetchData]);

  const deploy = async () => {
    const deploymentCost = railwayStatus?.deployment_cost_credits || 40;
    if ((user?.credits || 0) < deploymentCost) {
      toast.error(`Necesitas ${deploymentCost} créditos. Tienes: ${(user?.credits || 0).toFixed(2)}`);
      return;
    }
    
    if (!window.confirm(`Se descontarán ${deploymentCost} créditos. ¿Continuar?`)) return;
    
    setDeploying(true);
    try {
      const token = localStorage.getItem('cloudtexa_token');
      const res = await axios.post(
        `${API}/api/railway/deploy/${projectId}`,
        { project_id: projectId, env_variables: envVars, port: 3000 },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(res.data.message);
      refreshUser();
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al desplegar');
    }
    setDeploying(false);
  };

  const deleteDep = async (id) => {
    if (!window.confirm('¿Eliminar este despliegue de Railway?')) return;
    try {
      const token = localStorage.getItem('cloudtexa_token');
      await axios.delete(`${API}/api/railway/deployments/${id}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Despliegue eliminado');
      setDeployments(p => p.filter(d => d.id !== id));
    } catch {
      toast.error('Error al eliminar');
    }
  };

  const redeploy = async (id) => {
    try {
      const token = localStorage.getItem('cloudtexa_token');
      await axios.post(`${API}/api/railway/deployments/${id}/redeploy`, {}, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Redeploy iniciado');
      fetchData();
    } catch {
      toast.error('Error al redesplegar');
    }
  };

  const addEnvVar = () => {
    if (!newVarKey.trim()) return;
    setEnvVars(prev => ({ ...prev, [newVarKey]: newVarValue }));
    setNewVarKey('');
    setNewVarValue('');
  };

  const removeEnvVar = (key) => {
    setEnvVars(prev => {
      const copy = { ...prev };
      delete copy[key];
      return copy;
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-6 h-6 animate-spin text-purple-400" />
      </div>
    );
  }

  const deploymentCost = railwayStatus?.deployment_cost_credits || 40;
  const hasEnoughCredits = (user?.credits || 0) >= deploymentCost;
  const isConfigured = railwayStatus?.configured;

  return (
    <div className="h-full flex flex-col bg-slate-900" data-testid="railway-panel">
      {/* Header */}
      <div className="p-4 border-b border-slate-700">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Train className="w-5 h-5 text-purple-400" />
            <h2 className="font-medium text-white">Despliegue en Railway</h2>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-white">✕</button>
        </div>
        
        {/* Credits Info */}
        <div className="mt-3 p-3 rounded-lg bg-slate-800/50 border border-slate-700 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Coins className="w-4 h-4 text-amber-400" />
            <span className="text-sm text-gray-300">
              Tus créditos: <span className="text-white font-medium">{(user?.credits || 0).toFixed(2)}</span>
            </span>
          </div>
          <span className={`text-sm ${hasEnoughCredits ? 'text-green-400' : 'text-red-400'}`}>
            Costo: {deploymentCost} créditos
          </span>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-4 space-y-6">
        {/* Status */}
        {!isConfigured ? (
          <div className="p-4 rounded-lg bg-amber-500/10 border border-amber-500/30">
            <p className="text-sm text-amber-300">⚠️ Railway no está configurado</p>
            <p className="text-xs text-amber-200/70 mt-1">
              Añade RAILWAY_API_TOKEN en las variables de entorno del backend.
            </p>
          </div>
        ) : (
          <div className="p-3 rounded-lg bg-purple-500/10 border border-purple-500/30 flex items-start gap-3">
            <Train className="w-5 h-5 text-purple-400 mt-0.5" />
            <div>
              <p className="text-sm text-purple-300">Railway conectado</p>
              <p className="text-xs text-purple-200/70 mt-1">
                Usuario: {railwayStatus?.user || 'Conectado'}
              </p>
            </div>
          </div>
        )}

        {/* Environment Variables */}
        <div className="p-4 rounded-xl bg-slate-800/50 border border-slate-700">
          <h3 className="text-sm font-medium text-white mb-3">Variables de Entorno</h3>
          
          {Object.keys(envVars).length > 0 && (
            <div className="space-y-2 mb-3">
              {Object.entries(envVars).map(([key, value]) => (
                <div key={key} className="flex items-center justify-between p-2 rounded bg-slate-900/50">
                  <span className="text-xs text-gray-300 font-mono">{key}={value.slice(0, 20)}...</span>
                  <button onClick={() => removeEnvVar(key)} className="text-red-400 hover:text-red-300">
                    <Trash2 className="w-3 h-3" />
                  </button>
                </div>
              ))}
            </div>
          )}
          
          <div className="flex gap-2">
            <input
              type="text"
              placeholder="KEY"
              value={newVarKey}
              onChange={(e) => setNewVarKey(e.target.value)}
              className="flex-1 px-3 py-2 rounded bg-slate-900 border border-slate-600 text-white text-sm"
            />
            <input
              type="text"
              placeholder="value"
              value={newVarValue}
              onChange={(e) => setNewVarValue(e.target.value)}
              className="flex-1 px-3 py-2 rounded bg-slate-900 border border-slate-600 text-white text-sm"
            />
            <button onClick={addEnvVar} className="p-2 rounded bg-purple-500/20 text-purple-400">
              <Plus className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Deploy Button */}
        <button
          onClick={deploy}
          disabled={deploying || !hasEnoughCredits || !isConfigured}
          className={`w-full py-3 rounded-xl font-medium flex items-center justify-center gap-2 disabled:opacity-50 ${
            hasEnoughCredits && isConfigured
              ? 'bg-gradient-to-r from-purple-500 to-pink-500 text-white'
              : 'bg-gray-700 text-gray-400 cursor-not-allowed'
          }`}
        >
          {deploying ? <Loader2 className="w-5 h-5 animate-spin" /> : <Play className="w-5 h-5" />}
          {deploying ? 'Desplegando en Railway...' : hasEnoughCredits ? `Desplegar (${deploymentCost} créditos)` : 'Créditos insuficientes'}
        </button>

        {!hasEnoughCredits && (
          <p className="text-center text-sm text-amber-400">
            Necesitas {deploymentCost - (user?.credits || 0)} créditos más.
          </p>
        )}

        {/* Deployments List */}
        {deployments.length > 0 && (
          <div className="space-y-3">
            <h3 className="text-sm font-medium text-gray-400">Despliegues en Railway</h3>
            {deployments.map(dep => (
              <DeploymentItem 
                key={dep.id} 
                dep={dep} 
                onDelete={deleteDep} 
                onRedeploy={redeploy}
                onRefresh={fetchData}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default RailwayPanel;
